import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { Button, Loading } from '@linktivity/link-ui';

import SortIcon from '@link/shinkansen/assets/sort.svg?react';
import { useStore } from '@link/shinkansen/stores';
import {
  Diagram,
  RemainStatus,
  Station,
  TimeRange
} from '@link/shinkansen/services/types';

import StationPicker from './StationPicker';
import styles from './trainList.module.css';

interface StationPickerState {
  stations: Station[];
  selectedCode?: string;
  excludeCode?: string;
  onSelect: (station: Station) => void;
}

/** Presentation metadata for each seat-availability status. */
const STATUS_META: Record<
  RemainStatus,
  { symbol: string; tone: string; textKey: string; actionable: boolean }
> = {
  [RemainStatus.Available]: {
    symbol: '○',
    tone: styles.available,
    textKey: 'views.trainList.seatAvailable',
    actionable: true
  },
  [RemainStatus.Limited]: {
    symbol: '△',
    tone: styles.limited,
    textKey: 'views.trainList.seatLimited',
    actionable: true
  },
  [RemainStatus.SoldOut]: {
    symbol: '✕',
    tone: styles.soldOut,
    textKey: 'views.trainList.soldOut',
    actionable: false
  },
  [RemainStatus.Unknown]: {
    symbol: '—',
    tone: styles.notOnSale,
    textKey: 'views.trainList.notOnSale',
    actionable: false
  }
};

const rangeKey = (range: TimeRange) => `${range.from}-${range.to}`;

/** Join station names with `/` (e.g. 东京/品川) for the route display. */
const joinStationNames = (stations: Station[]) =>
  stations.map(station => station.name).join('/');

/** `06:00` → `6:00`; drops the leading zero on the hour for tab labels. */
const trimHour = (time: string) => time.replace(/^0/, '');

/**
 * The tab label. The API's `to` is the inclusive end of the window (e.g.
 * `08:59`); round it up to the next boundary so the tab reads `6:00-9:00`.
 */
const formatRangeLabel = (range: TimeRange) => {
  const [hours, minutes] = range.to.split(':').map(Number);
  const total = hours * 60 + minutes + 1;
  const to = `${Math.floor(total / 60) % 24}:${String(total % 60).padStart(2, '0')}`;
  return `${trimHour(range.from)}-${to}`;
};

const TrainListView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const tUrl = searchParams.get('t_url');
  const { shinkansen } = useStore();
  const {
    bookingInfo,
    diagrams,
    selection,
    depStations,
    arrStations,
    orderableTimeRanges
  } = shinkansen;
  const [stationPicker, setStationPicker] = useState<StationPickerState | null>(
    null
  );
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  const [diagramsLoading, setDiagramsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      shinkansen.setToken(token);
    }
    if (bookingId) {
      shinkansen.setBookingId(bookingId);
    }
    if (tUrl) {
      shinkansen.setTUrl(tUrl);
    }
  }, [token, bookingId, tUrl, shinkansen]);

  useEffect(() => {
    // Refetch when there's no booking loaded yet, or when the URL bookingId
    // points at a different booking than the one currently loaded (stale data).
    const targetBookingId = bookingId || shinkansen.bookingId || undefined;
    if (
      !bookingInfo ||
      (targetBookingId && bookingInfo.bookingId !== targetBookingId)
    ) {
      void shinkansen.getBookingData(targetBookingId);
    }
  }, [bookingInfo, bookingId, shinkansen]);

  // Stations ship inside the order detail (loaded by getBookingData). Once the
  // booking is loaded, fetch the orderable departure-time windows (the
  // time-range tabs) via getStations.
  const fromCode = selection.fromStation?.code;
  const toCode = selection.toStation?.code;
  const departDate = bookingInfo?.departDate;
  useEffect(() => {
    if (!bookingInfo) {
      return;
    }
    void shinkansen.getStations().catch(() => undefined);
  }, [bookingInfo, shinkansen]);

  const timeRanges = orderableTimeRanges;

  // Don't default-select a range; only drop the current pick if it's no longer
  // offered (e.g. the ranges changed after a booking swap).
  useEffect(() => {
    setTimeRange(current =>
      current && timeRanges.some(range => rangeKey(range) === rangeKey(current))
        ? current
        : null
    );
  }, [timeRanges]);

  // Fetch the train list only after a time range is picked, scoped to that
  // window (depTimeFrom / depTimeTo). Re-run on station swap / picker changes.
  useEffect(() => {
    if (!timeRange || !fromCode || !toCode || !departDate) {
      setDiagramsLoading(false);
      return;
    }
    setDiagramsLoading(true);
    void shinkansen
      .searchDiagrams(timeRange)
      // On failure, clear so a stale list from a previous selection can't show.
      .catch(() => shinkansen.setDiagrams([]))
      .finally(() => setDiagramsLoading(false));
  }, [timeRange, fromCode, toCode, departDate, shinkansen]);

  const handleSelectTrain = (diagram: Diagram) => {
    shinkansen.setDiagram(diagram);
    // Relative nav keeps the /:token/:bookingId prefix (→ .../seat).
    navigate('seat');
  };

  if (!bookingInfo) {
    // No booking id at all (e.g. index route without params) — don't spin
    // forever; show an empty state instead.
    if (!bookingId && !shinkansen.bookingId) {
      return <p className={styles.empty}>{t('common.noBooking')}</p>;
    }
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  const { fromStation, toStation, routeReversed } = selection;
  const departureStationOptions = routeReversed ? arrStations : depStations;
  const arrivalStationOptions = routeReversed ? depStations : arrStations;
  const handleSwapStations = () => {
    if (diagramsLoading) {
      return;
    }
    setDiagramsLoading(true);
    shinkansen.swapStations();
  };
  const openDepartureStationPicker = () => {
    setStationPicker({
      stations: departureStationOptions,
      selectedCode: fromStation?.code,
      excludeCode: toStation?.code,
      onSelect: station => {
        if (toStation) {
          shinkansen.setStations(station, toStation);
        }
        setStationPicker(null);
      }
    });
  };
  const openArrivalStationPicker = () => {
    setStationPicker({
      stations: arrivalStationOptions,
      selectedCode: toStation?.code,
      excludeCode: fromStation?.code,
      onSelect: station => {
        if (fromStation) {
          shinkansen.setStations(fromStation, station);
        }
        setStationPicker(null);
      }
    });
  };

  return (
    <div className={styles.container}>
      {/* 车辆信息 */}
      <h1 className={styles.sectionTitle}>
        {t('views.trainList.vehicleInfo')}
      </h1>
      <div className={styles.vehicleCard}>
        <span className={styles.vehicleTicketName}>
          {t('views.trainList.ticketName')}
        </span>
        <div className={styles.vehicleRow}>
          <span className={styles.vehicleLabel}>
            {t('views.trainList.travelDate')}
          </span>
          <span className={styles.vehicleValue}>{bookingInfo.departDate}</span>
        </div>
        <div className={styles.vehicleRow}>
          <span className={styles.vehicleLabel}>
            {t('views.trainList.route')}
          </span>
          <span className={styles.vehicleValue}>
            {joinStationNames(depStations)} → {joinStationNames(arrStations)}
          </span>
        </div>
        {!!bookingInfo.units?.length && (
          <div className={styles.vehicleRow}>
            <span className={styles.vehicleLabel}>
              {t('views.trainList.ticketCount')}
            </span>
            <span className={styles.vehicleValue}>
              {bookingInfo.units
                .map(unit => `${unit.name} ×${unit.quantity ?? 0}`)
                .join(' ')}
            </span>
          </div>
        )}
        <div className={styles.vehicleRow}>
          <span className={styles.vehicleLabel}>
            {t('views.trainList.seatClass')}
          </span>
          <span className={styles.vehicleValue}>
            {bookingInfo.seatClassName}
          </span>
        </div>
        <p className={styles.vehicleNote}>
          {t('views.trainList.preOrderNote')}
        </p>
      </div>

      {/* 选择车站 */}
      <h1 className={styles.sectionTitle}>
        {t('views.trainList.selectStation')}
      </h1>
      <div className={styles.stationCard}>
        <button
          type="button"
          className={styles.station}
          onClick={openDepartureStationPicker}
        >
          <span className={styles.stationLabel}>
            {t('views.trainList.from')}
          </span>
          <span className={styles.stationNameRow}>
            <span className={styles.stationName}>{fromStation?.name}</span>
          </span>
        </button>
        <button
          type="button"
          className={styles.swap}
          aria-label={`${t('views.trainList.from')} ⇌ ${t('views.trainList.to')}`}
          onClick={handleSwapStations}
        >
          <SortIcon className={styles.swapIcon} />
        </button>
        <button
          type="button"
          className={clsx(styles.station, styles.stationEnd)}
          onClick={openArrivalStationPicker}
        >
          <span className={styles.stationLabel}>{t('views.trainList.to')}</span>
          <span className={styles.stationNameRow}>
            <span className={styles.stationName}>{toStation?.name}</span>
          </span>
        </button>
      </div>

      {/* 选择车次 */}
      <h1 className={styles.sectionTitle}>
        {t('views.trainList.selectTrain')}
      </h1>
      <div className={styles.timeTags}>
        {timeRanges.map(range => {
          const active = timeRange
            ? rangeKey(timeRange) === rangeKey(range)
            : false;
          return (
            <button
              key={rangeKey(range)}
              type="button"
              aria-pressed={active}
              className={clsx(styles.timeTag, active && styles.timeTagActive)}
              onClick={() => setTimeRange(range)}
            >
              {formatRangeLabel(range)}
            </button>
          );
        })}
      </div>

      <div className={styles.trainList}>
        {timeRange && diagramsLoading && (
          <div className={styles.trainListLoading}>
            <Loading />
          </div>
        )}
        {timeRange && !diagramsLoading && diagrams.length === 0 && (
          <p className={styles.empty}>{t('views.trainList.noTrains')}</p>
        )}
        {timeRange &&
          !diagramsLoading &&
          diagrams.map(diagram => {
            const meta =
              STATUS_META[diagram.remainStatus ?? RemainStatus.Unknown];
            return (
              <div
                key={diagram.diagramCode}
                className={clsx(
                  styles.trainCard,
                  !meta.actionable && styles.trainCardDisabled
                )}
              >
                <div className={styles.trainRow}>
                  <div className={styles.trainTime}>
                    <span className={styles.timeText}>
                      {diagram.departTime}
                    </span>
                    <span className={styles.stationText}>
                      {fromStation?.name}
                    </span>
                  </div>
                  <div className={styles.trainMiddle}>
                    <span className={styles.duration}>
                      {diagram.timeDuration}
                    </span>
                    <div className={styles.line} />
                    <span className={styles.trainName}>
                      {diagram.trainName}
                    </span>
                  </div>
                  <div className={styles.trainTime}>
                    <span className={styles.timeText}>
                      {diagram.arriveTime}
                    </span>
                    <span className={styles.stationText}>
                      {toStation?.name}
                    </span>
                  </div>
                </div>

                <div className={styles.bookingRow}>
                  <span className={clsx(styles.availability, meta.tone)}>
                    <span className={styles.symbol}>{meta.symbol}</span>
                    {t(meta.textKey)}
                  </span>
                  <Button
                    variant="filled"
                    className={styles.selectButton}
                    disabled={!meta.actionable}
                    onClick={() => handleSelectTrain(diagram)}
                  >
                    {meta.actionable
                      ? t('views.trainList.select')
                      : t('views.trainList.book')}
                  </Button>
                </div>
              </div>
            );
          })}
      </div>

      <StationPicker
        visible={stationPicker !== null}
        title={t('views.trainList.selectStation')}
        stations={stationPicker?.stations ?? []}
        selectedCode={stationPicker?.selectedCode}
        excludeCode={stationPicker?.excludeCode}
        onSelect={station => stationPicker?.onSelect(station)}
        onClose={() => setStationPicker(null)}
      />
    </div>
  );
};

const TrainList = observer(TrainListView);

export default TrainList;
